import {
  AIRulePreview,
  RuleCardDropdown,
  type RuleCardDropdownProps,
  RuleDeletionAlert,
  RulesContent,
} from '@carboncopyco/editor-ui';
import type {
  FindReplaceRule,
  GroupSchemaType,
  Modification,
  Selector,
} from '@carboncopyco/types';
import {
  ColorHex,
  ExpandDownIcon,
  Separator,
  TextMenuHeader,
  notify,
} from '@carboncopyco/ui';
import { useBoolean } from 'ahooks';
import classNames from 'classnames';
import { useEffect, useMemo, useRef } from 'react';
import { useEditor } from '../../../context/EditorSdkProvider';
import {
  FindAndReplaceRuleForm,
  type FindAndReplaceRuleFormProps,
} from '../../FindAndReplaceRuleForm';
import { FindAndReplaceRulePreview } from '../FindAndReplaceRulePreview';

export interface FindAndReplaceRuleListItemProps
  extends Pick<FindAndReplaceRuleFormProps, 'rule' | 'rules'> {
  onEdit?: (rule: FindReplaceRule, selectors?: Selector[]) => Promise<unknown>;
  onDelete?: (rule: FindReplaceRule) => void;
  open: boolean;
  onOpenRule: () => void;
  onCloseRule: () => void;
}

export const GroupAiMenuTitle = ({
  rule,
  onClick,
  open,
  onDelete,
}: {
  rule: Modification<GroupSchemaType.aiPromptGroup>;
  onClick?: () => void;
  open: boolean;
  onDelete?: (rule: FindReplaceRule) => void;
}) => {
  const editorSdk = useEditor();
  const [showDeletionAlert, setShowDeletionAlert] = useBoolean();

  const dropdownItems = useMemo(() => {
    const res = [];
    if (onDelete) {
      res.push({
        type: 'delete',
        visible: !!onDelete,
        onClick: () => {
          setShowDeletionAlert.setTrue();
        },
      });
    }
    return res as RuleCardDropdownProps['items'];
  }, [rule]);
  return (
    <div
      className={classNames('flex justify-between w-full')}
      onClick={onClick}
      key={rule.content.id}
    >
      <div className="flex-grow shrink min-w-0">
        <AIRulePreview
          isHighlighted={open}
          rule={rule}
          getAllSubGroupModifications={editorSdk.getAllSubGroupModifications}
        />
      </div>
      {!!Object.keys(dropdownItems).length && (
        <RuleCardDropdown items={dropdownItems} />
      )}
      <div className={classNames(open && 'rotate-180')}>
        <ExpandDownIcon
          color={ColorHex.natural600}
          size={24}
        />
      </div>
      <RuleDeletionAlert
        open={showDeletionAlert}
        onClose={setShowDeletionAlert.setFalse}
        onErrorMessage="Failed to delete"
        onConfirm={async () => {
          await editorSdk.deleteAiPromptModification(rule);
          setShowDeletionAlert.setFalse();
        }}
        textLabel="Deleting this group will also remove the associated rule created by the AI
        Wizard tool. Are you sure you want to delete?"
        rulePreview={
          <AIRulePreview
            rule={rule}
            getAllSubGroupModifications={editorSdk.getAllSubGroupModifications}
          />
        }
      />
    </div>
  );
};

export const FindAndReplaceRuleListItemGroup = ({
  rule,
  onDelete,
  onEdit,
  open,
}: {
  rule: Modification<GroupSchemaType.aiPromptGroup> & {
    children: FindReplaceRule[];
  };
  onEdit?: (rule: FindReplaceRule, selectors?: Selector[]) => Promise<unknown>;
  onDelete?: (rule: FindReplaceRule) => void;
  open: boolean;
}) => {
  return (
    <RulesContent.List.Item
      withBackground={false}
      expandable={true}
      key={rule.content.id}
      headerSelected={open}
      id={rule.content.id}
      header={
        <GroupAiMenuTitle
          rule={rule}
          open={open}
          onDelete={onDelete}
        />
      }
      content={
        <RulesContent>
          <RulesContent.List
            withLeftBorder={true}
            rules={rule.children.map((rule) => ({
              id: rule.ruleId,
              rule,
            }))}
            draggable={false}
            renderItem={({ ruleItem, open, openRule, closeRule }) => (
              <FindAndReplaceRuleListItem
                onDelete={onDelete}
                onEdit={onEdit}
                rules={rule.children}
                key={ruleItem.id}
                rule={ruleItem.rule as FindReplaceRule}
                open={open}
                onOpenRule={openRule}
                onCloseRule={closeRule}
              />
            )}
          />
          <Separator horizontal />
        </RulesContent>
      }
      action={null}
    />
  );
};

export const FindAndReplaceRuleListItem = ({
  open,
  onOpenRule,
  onCloseRule,
  rule,
  rules,
  onEdit,
  onDelete,
}: FindAndReplaceRuleListItemProps) => {
  const [autoFocus, setAutoFocus] = useBoolean();
  const prevRules = useRef<FindReplaceRule[]>(rules);

  useEffect(() => {
    if (prevRules.current?.length !== rules.length) {
      onCloseRule();
      prevRules.current = rules;
    }
  }, [rules, onCloseRule]);

  const dropdownItems = useMemo(() => {
    const res = [];
    if (onEdit) {
      res.push({
        type: 'edit',
        visible: !!onEdit,
        onClick: () => {
          onOpenRule();
          setAutoFocus.setTrue();
        },
      });
    }
    if (onDelete) {
      res.push({
        type: 'delete',
        visible: !!onDelete,
        onClick: () => {
          onDelete(rule);
        },
      });
    }
    return res as RuleCardDropdownProps['items'];
  }, [rule, onDelete, onOpenRule, setAutoFocus]);

  return (
    <RulesContent.List.Item
      expandable={true}
      id={rule.ruleId}
      header={
        open ? (
          <TextMenuHeader
            label="Rule settings"
            color="text-ds-natural-600"
          />
        ) : (
          <FindAndReplaceRulePreview
            rule={rule}
            layout="grid"
          />
        )
      }
      content={
        <FindAndReplaceRuleForm
          autoFocus={autoFocus}
          onFocus={() => setTimeout(setAutoFocus.setFalse, 300)}
          rule={rule}
          rules={rules}
          type={rule.type}
          onSubmit={onEdit}
          onSuccess={() => {
            // a small delay before the closing animation
            setTimeout(onCloseRule, 300);
          }}
          onError={() => notify.error('Failed to edit rule, please try again')}
          onCancel={onCloseRule}
        />
      }
      action={
        Object.keys(dropdownItems).length ? (
          <RuleCardDropdown items={dropdownItems} />
        ) : undefined
      }
    />
  );
};
